'use client';

import { createClient } from '@supabase/supabase-js';

const supabaseUrl = 'https://evaopijcfrbtztxkjwqp.supabase.co';
const supabaseKey = 'sb_publishable_8-N2gkBJRiXQekSH5atFNw_GPCSaCLW';
const supabase = createClient(supabaseUrl, supabaseKey);

// ─── Paddle interface ─────────────────────────────────────────────────────────
export interface PaddleSpec {
  label: string;
  value: string | boolean | number;
  unit?: string;
  description?: string;
}

export interface PaddleCategory {
  name: string;
  icon: string;
  specs: PaddleSpec[];
}

export interface Paddle {
  id: string;
  slug: string;
  name: string;
  brand: string;
  image: string;
  imageAlt: string;
  score: number;
  price: string;
  priceFormatted: string;
  discountCode?: string;
  affiliateLink?: string;
  year?: string;
  releaseYear: number;
  isNew?: boolean;
  isWinner?: boolean;
  tagline: string;
  categories: PaddleCategory[];
  pros: string[];
  cons: string[];
  rating: number;
  reviewCount: number;
  skillLevel: 'Beginner' | 'Intermediate' | 'Advanced' | 'All Levels';
  playStyle: string[];
  approvalBody?: string;
  shape?: string;
  gritType?: string;
  buildType?: string;
  paddleType?: string;
  coreThickness?: number;
  gripLength?: number;
  gripSize?: number;
  weight?: number;
  swingweight?: number;
  swingweightPercentile?: number;
  twistweight?: number;
  twistweightPercentile?: number;
  balancePoint?: number;
  spinRating?: string;
  spin?: number;
  power?: number;
  powerPercentile?: number;
  pop?: number;
  popPercentile?: number;
  firepowerPercentile?: number;
  firepowerZ?: number;
  firepowerZTier?: string;
  firepowerTier?: string;
}

export interface CategoryInfo {
  name: string;
  slug: string;
  icon: string;
  count: number;
  description: string;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────
function slugify(name: string): string {
  return name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
}

function scoreFromTier(tier: string, power?: number, spin?: number): number {
  const base: Record<string, number> = {
    'Firepower Elite': 95,
    'Firepower High': 87,
    'Firepower Balanced': 80,
    'Firepower Control+': 74,
    'Firepower Soft': 68,
  };
  let s = base[tier] ?? 75;
  if (power && !isNaN(power)) s = Math.min(99, s + Math.round((power - 55) * 0.8));
  if (spin && !isNaN(spin)) s = Math.min(99, s + Math.round((spin - 1950) * 0.01));
  const result = Math.max(60, Math.min(99, s));
  return isNaN(result) ? 75 : result;
}

function skillFromType(paddleType: string, priceStr: string): 'Beginner' | 'Intermediate' | 'Advanced' | 'All Levels' {
  const price = parseFloat(priceStr) || 0;
  if (price < 100) return 'Beginner';
  if (paddleType === 'Control') return 'Advanced';
  if (paddleType === 'Power') return 'Advanced';
  if (price > 220) return 'Advanced';
  return 'Intermediate';
}

function prosFromData(paddleType: string, tier: string, shape: string): string[] {
  const pros: string[] = [];
  if (tier === 'Firepower Elite') pros.push('Elite firepower performance');
  if (tier === 'Firepower High') pros.push('High power output');
  if (paddleType === 'Control') pros.push('Excellent touch and control');
  if (paddleType === 'Power') pros.push('Powerful drives and smashes');
  if (paddleType === 'All-Court' || paddleType === 'All Court') pros.push('Versatile all-court performance');
  if (shape === 'Elongated') pros.push('Extended reach with elongated shape');
  if (shape === 'Widebody') pros.push('Large sweet spot');
  if (shape === 'Hybrid') pros.push('Balanced hybrid shape');
  if (pros.length < 2) pros.push('Quality construction');
  return pros.slice(0, 4);
}

function consFromData(priceStr: string, tier: string): string[] {
  const price = parseFloat(priceStr) || 0;
  const cons: string[] = [];
  if (price > 250) cons.push('Premium price point');
  if (tier === 'Firepower Elite' || tier === 'Firepower High') cons.push('Less forgiving for beginners');
  if (tier === 'Firepower Soft') cons.push('Lower power ceiling');
  if (cons.length < 2) cons.push('May require adjustment period');
  return cons.slice(0, 3);
}

function deterministicReviewCount(brand: string, name: string): number {
  const str = `${brand}${name}`;
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = (hash * 31 + str.charCodeAt(i)) >>> 0;
  }
  return (hash % 191) + 10;
}

function buildCategorySpecs(p: {
  coreThickness?: number; weight?: number; gripLength?: number; gripSize?: number;
  approvalBody?: string; shape?: string; spin?: number; power?: number; pop?: number;
  swingweight?: number; twistweight?: number; balancePoint?: number;
}): PaddleCategory[] {
  return [
    {
      name: 'Performance',
      icon: 'Performance',
      specs: [
        { label: 'Spin (RPM)', value: p.spin ? `${p.spin}` : 'N/A', description: 'Measured spin rate' },
        { label: 'Power (MPH)', value: p.power ? `${p.power}` : 'N/A', description: 'Ball exit speed' },
        { label: 'Pop (MPH)', value: p.pop ? `${p.pop}` : 'N/A', description: 'Pop speed off paddle' },
        { label: 'Swingweight', value: p.swingweight ?? 'N/A', description: 'Resistance to swing' },
      ],
    },
    {
      name: 'Construction',
      icon: 'Construction',
      specs: [
        { label: 'Core Thickness', value: p.coreThickness ? `${p.coreThickness}mm` : 'N/A', description: 'Core thickness in mm' },
        { label: 'Shape', value: p.shape ?? 'N/A', description: 'Paddle shape' },
        { label: 'Approval Body', value: p.approvalBody ?? 'N/A', description: 'Regulatory approval' },
        { label: 'Twistweight', value: p.twistweight ?? 'N/A', description: 'Resistance to twisting' },
      ],
    },
    {
      name: 'Dimensions',
      icon: 'Dimensions',
      specs: [
        { label: 'Weight', value: p.weight ? `${p.weight} oz` : 'N/A', description: 'Paddle weight' },
        { label: 'Grip Length', value: p.gripLength ? `${p.gripLength} in` : 'N/A', description: 'Handle length' },
        { label: 'Grip Size', value: p.gripSize ? `${p.gripSize} in` : 'N/A', description: 'Handle circumference' },
        { label: 'Balance Point', value: p.balancePoint ? `${p.balancePoint}mm` : 'N/A', description: 'Balance point from butt' },
      ],
    },
    {
      name: 'Features',
      icon: 'Features',
      specs: [
        { label: 'USAP Approved', value: (p.approvalBody ?? '').includes('USAP'), description: 'Approved for USAP tournaments' },
        { label: 'UPA-A Approved', value: (p.approvalBody ?? '').includes('UPA-A'), description: 'Approved for UPA-A tournaments' },
        { label: 'Elongated Shape', value: p.shape === 'Elongated' || p.shape === 'Extra-elongated', description: 'Extended reach design' },
        { label: 'Widebody Shape', value: p.shape === 'Widebody', description: 'Larger sweet spot design' },
      ],
    },
  ];
}

// ─── Public API ───────────────────────────────────────────────────────────────

// All Supabase columns are TEXT strings - handle accordingly:
// paddle_name, brand, shape, grit_type, build_type, paddle_type,
// approval_body, year_released, spin_rating, firepower_z_tier,
// discount_code, link_to_paddle, image_url
//
// TEXT but contain "$" prefix: price (e.g. "$209.99")
//
// TEXT but contain "%" suffix:
// swingweight_percentile, twistweight_percentile,
// power_percentile, pop_percentile, firepower_percentile
//
// Plain numeric TEXT (no symbols):
// core_thickness_mm, grip_length_in, grip_size_in, weight_oz,
// swingweight, twistweight, balance_point_mm,
// spin_rpm, power_mph, pop_mph, firepower_z

let _fetchAllPaddlesPromise: Promise<Paddle[]> | null = null;

/** Fetch all paddles from Supabase — cached so only one network request fires */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function fetchAllPaddles(): Promise<Paddle[]> {
  if (_fetchAllPaddlesPromise) return _fetchAllPaddlesPromise;
  _fetchAllPaddlesPromise = (async () => {
  const { data, error } = await supabase.from('paddles').select('*');
  if (error) throw error;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return (data ?? []).map((row: any) => {
    const mapped = {
      id: row.id,
      name: row.paddle_name ?? '',
      brand: row.brand ?? '',
      price: row.price ?? '$0.00',           // keep as plain text string, never parse
      discountCode: row.discount_code ?? '',
      affiliateLink: row.link_to_paddle ?? '',
      image: row.image_url ?? '',             // use directly as <img src>
      year: row.year_released ?? '',
      approvalBody: row.approval_body ?? '',
      shape: row.shape ?? '',
      gritType: row.grit_type ?? '',
      buildType: row.build_type ?? '',
      paddleType: row.paddle_type ?? '',
      coreThickness: parseFloat(row.core_thickness_mm) || 0,
      gripLength: parseFloat(row.grip_length_in) || 0,
      gripSize: parseFloat(row.grip_size_in) || 0,
      weight: parseFloat(row.weight_oz) || 0,
      swingweight: parseFloat(row.swingweight) || 0,
      swingweightPercentile: parseFloat(row.swingweight_percentile) || 0,
      twistweight: parseFloat(row.twistweight) || 0,
      twistweightPercentile: parseFloat(row.twistweight_percentile) || 0,
      balancePoint: parseFloat(row.balance_point_mm) || 0,
      spinRating: row.spin_rating ?? '',
      spin: parseFloat(row.spin_rpm) || 0,
      power: parseFloat(row.power_mph) || 0,
      powerPercentile: parseFloat(row.power_percentile) || 0,
      pop: parseFloat(row.pop_mph) || 0,
      popPercentile: parseFloat(row.pop_percentile) || 0,
      firepowerPercentile: parseFloat(row.firepower_percentile) || 0,
      firepowerZ: parseFloat(row.firepower_z) || 0,
      firepowerZTier: row.firepower_z_tier ?? '',
    };

    // Derive computed fields needed by the rest of the app
    const score = scoreFromTier(mapped.firepowerZTier, mapped.power, mapped.spin);
    const safeScore = isNaN(score) || !isFinite(score) ? 70 : score;
    const releaseYear = parseInt(String(mapped.year), 10) || 0;
    const imageUrl = mapped.image && String(mapped.image).trim() !== ''
      ? mapped.image
      : '/assets/images/no_image.png';

    return {
      id: `paddle-${mapped.id}`,
      slug: slugify(`${mapped.brand}-${mapped.name}`),
      name: mapped.name,
      brand: mapped.brand,
      image: imageUrl,
      imageAlt: `${mapped.brand} ${mapped.name} pickleball paddle`,
      score: safeScore,
      price: mapped.price,
      priceFormatted: mapped.price || 'N/A',
      discountCode: mapped.discountCode || undefined,
      affiliateLink: mapped.affiliateLink || undefined,
      year: mapped.year || undefined,
      releaseYear,
      isNew: releaseYear >= 2025,
      isWinner: mapped.firepowerZTier === 'Firepower Elite',
      tagline: `${mapped.paddleType} paddle with ${mapped.shape.toLowerCase()} shape`,
      categories: buildCategorySpecs({
        coreThickness: mapped.coreThickness,
        weight: mapped.weight,
        gripLength: mapped.gripLength,
        gripSize: mapped.gripSize,
        approvalBody: mapped.approvalBody,
        shape: mapped.shape,
        spin: mapped.spin,
        power: mapped.power,
        pop: mapped.pop,
        swingweight: mapped.swingweight,
        twistweight: mapped.twistweight,
        balancePoint: mapped.balancePoint,
      }),
      pros: prosFromData(mapped.paddleType, mapped.firepowerZTier, mapped.shape),
      cons: consFromData(mapped.price, mapped.firepowerZTier),
      rating: Math.round((safeScore / 20) * 10) / 10,
      reviewCount: deterministicReviewCount(mapped.brand, mapped.name),
      skillLevel: skillFromType(mapped.paddleType, mapped.price),
      playStyle: mapped.paddleType ? [mapped.paddleType] : [],
      approvalBody: mapped.approvalBody || undefined,
      shape: mapped.shape || undefined,
      gritType: mapped.gritType || undefined,
      buildType: mapped.buildType || undefined,
      paddleType: mapped.paddleType || undefined,
      coreThickness: mapped.coreThickness || undefined,
      gripLength: mapped.gripLength || undefined,
      gripSize: mapped.gripSize || undefined,
      weight: mapped.weight || undefined,
      swingweight: mapped.swingweight || undefined,
      swingweightPercentile: mapped.swingweightPercentile || undefined,
      twistweight: mapped.twistweight || undefined,
      twistweightPercentile: mapped.twistweightPercentile || undefined,
      balancePoint: mapped.balancePoint || undefined,
      spinRating: mapped.spinRating || undefined,
      spin: mapped.spin || undefined,
      power: mapped.power || undefined,
      powerPercentile: mapped.powerPercentile || undefined,
      pop: mapped.pop || undefined,
      popPercentile: mapped.popPercentile || undefined,
      firepowerPercentile: mapped.firepowerPercentile || undefined,
      firepowerZ: mapped.firepowerZ || undefined,
      firepowerZTier: mapped.firepowerZTier || undefined,
      firepowerTier: mapped.firepowerZTier || undefined,
    } satisfies Paddle;
  });
  })();
  return _fetchAllPaddlesPromise;
}

/** Fetch a single paddle by slug */
export async function fetchPaddleBySlug(slug: string): Promise<Paddle | null> {
  const all = await fetchAllPaddles();
  return all.find(p => p.slug === slug) ?? null;
}

/** Fetch top-ranked paddles (sorted by score, top 20) */
export async function fetchTopPaddles(): Promise<Paddle[]> {
  const all = await fetchAllPaddles();
  return [...all].sort((a, b) => b.score - a.score).slice(0, 20);
}

/** Build category list from a paddle array */
export function buildCategories_list(paddles: Paddle[]): CategoryInfo[] {
  return [
    { name: 'Power Paddles', slug: 'power', icon: 'Power', count: paddles.filter(p => p.paddleType === 'Power').length, description: 'Maximum power and drives' },
    { name: 'Control Paddles', slug: 'control', icon: 'Control', count: paddles.filter(p => p.paddleType === 'Control').length, description: 'Precision and soft game' },
    { name: 'All-Court Paddles', slug: 'all-court', icon: 'All-Court', count: paddles.filter(p => p.paddleType === 'All-Court' || p.paddleType === 'All Court').length, description: 'Versatile all-around play' },
    { name: 'USAP Approved', slug: 'tournament', icon: 'Tournament', count: paddles.filter(p => (p.approvalBody ?? '').includes('USAP')).length, description: 'Tournament legal paddles' },
    { name: 'Elongated Shape', slug: 'elongated', icon: 'Elongated', count: paddles.filter(p => p.shape === 'Elongated').length, description: 'Extended reach design' },
    { name: 'Widebody Shape', slug: 'widebody', icon: 'Widebody', count: paddles.filter(p => p.shape === 'Widebody').length, description: 'Large sweet spot' },
  ];
}

/** Filter paddles by category slug */
export function filterByCategory(paddles: Paddle[], slug: string): Paddle[] {
  if (slug === 'power') return paddles.filter(p => p.paddleType === 'Power');
  if (slug === 'control') return paddles.filter(p => p.paddleType === 'Control');
  if (slug === 'all-court') return paddles.filter(p => p.paddleType === 'All-Court' || p.paddleType === 'All Court');
  if (slug === 'tournament') return paddles.filter(p => (p.approvalBody ?? '').includes('USAP'));
  if (slug === 'elongated') return paddles.filter(p => p.shape === 'Elongated');
  if (slug === 'widebody') return paddles.filter(p => p.shape === 'Widebody');
  return paddles;
}
